/**
 * RBAC (Role-Based Access Control) core for the Saee admin backoffice.
 */

/** Normalized app-level roles used by the admin dashboard. */
export type AppRole =
  | 'SYSTEM_ADMIN'
  | 'OPERATIONS_MANAGER'
  | 'FINANCIAL_OFFICER'
  | 'SUPPORT_AGENT';

/** Fine-grained permissions for every admin action / view. */
export type Permission =
  | 'CAN_VIEW_OVERVIEW'
  | 'CAN_VIEW_BROKERS'
  | 'CAN_CREATE_BROKER'
  | 'CAN_EDIT_BROKER'
  | 'CAN_EDIT_BROKER_CONTACT'
  | 'CAN_VIEW_DEALS'
  | 'CAN_CREATE_DEAL'
  | 'CAN_EDIT_DEAL'
  | 'CAN_DELETE_DEAL'
  | 'CAN_VIEW_REPORTS'
  | 'CAN_VIEW_LOGS'
  | 'CAN_VIEW_SETTINGS'
  | 'CAN_EDIT_SETTINGS'
  | 'CAN_MANAGE_ACCOUNTS'
  | 'CAN_RESET_PLATFORM'
  | 'CAN_MANAGE_PAYOUTS'
  | 'CAN_MANAGE_COMMISSION_RATES'
  | 'CAN_VIEW_SAEE_COMMISSION'
  | 'CAN_VIEW_TAX_INVOICE'
  | 'CAN_MANAGE_ROLES'
  | 'CAN_APPROVE_WITHDRAWALS';

/** Backend roles as returned by GET /api/auth/me. */
export type BackendRole =
  | 'system_administrator'
  | 'operations_manager'
  | 'financial_officer'
  | 'support_agent'
  | 'user'
  | string;

export const ALL_PERMISSIONS: Permission[] = [
  'CAN_VIEW_OVERVIEW',
  'CAN_VIEW_BROKERS',
  'CAN_CREATE_BROKER',
  'CAN_EDIT_BROKER',
  'CAN_EDIT_BROKER_CONTACT',
  'CAN_VIEW_DEALS',
  'CAN_CREATE_DEAL',
  'CAN_EDIT_DEAL',
  'CAN_DELETE_DEAL',
  'CAN_VIEW_REPORTS',
  'CAN_VIEW_LOGS',
  'CAN_VIEW_SETTINGS',
  'CAN_EDIT_SETTINGS',
  'CAN_MANAGE_ACCOUNTS',
  'CAN_RESET_PLATFORM',
  'CAN_MANAGE_PAYOUTS',
  'CAN_MANAGE_COMMISSION_RATES',
  'CAN_VIEW_SAEE_COMMISSION',
  'CAN_VIEW_TAX_INVOICE',
  'CAN_MANAGE_ROLES',
  'CAN_APPROVE_WITHDRAWALS',
];

const SYSTEM_ADMIN_PERMISSIONS: Permission[] = ALL_PERMISSIONS;

const OPERATIONS_MANAGER_PERMISSIONS: Permission[] = [
  'CAN_VIEW_OVERVIEW',
  'CAN_VIEW_BROKERS',
  'CAN_CREATE_BROKER',
  'CAN_EDIT_BROKER',
  'CAN_VIEW_DEALS',
  'CAN_CREATE_DEAL',
  'CAN_EDIT_DEAL',
  'CAN_DELETE_DEAL',
  'CAN_VIEW_REPORTS',
  'CAN_VIEW_LOGS',
  'CAN_VIEW_SETTINGS',
  'CAN_VIEW_TAX_INVOICE',
  'CAN_APPROVE_WITHDRAWALS',
];

const FINANCIAL_OFFICER_PERMISSIONS: Permission[] = [
  'CAN_VIEW_OVERVIEW',
  'CAN_VIEW_BROKERS',
  'CAN_VIEW_DEALS',
  'CAN_VIEW_REPORTS',
  'CAN_VIEW_SETTINGS',
  'CAN_MANAGE_PAYOUTS',
  'CAN_MANAGE_COMMISSION_RATES',
  'CAN_VIEW_SAEE_COMMISSION',
  'CAN_VIEW_TAX_INVOICE',
  'CAN_APPROVE_WITHDRAWALS',
];

const SUPPORT_AGENT_PERMISSIONS: Permission[] = [
  'CAN_VIEW_OVERVIEW',
  'CAN_VIEW_BROKERS',
  'CAN_VIEW_DEALS',
  'CAN_VIEW_SETTINGS',
  'CAN_EDIT_BROKER_CONTACT',
];

const ROLE_PERMISSION_MAP: Record<AppRole, Permission[]> = {
  SYSTEM_ADMIN: SYSTEM_ADMIN_PERMISSIONS,
  OPERATIONS_MANAGER: OPERATIONS_MANAGER_PERMISSIONS,
  FINANCIAL_OFFICER: FINANCIAL_OFFICER_PERMISSIONS,
  SUPPORT_AGENT: SUPPORT_AGENT_PERMISSIONS,
};

const BACKEND_TO_APP_ROLE: Record<string, AppRole> = {
  system_administrator: 'SYSTEM_ADMIN',
  operations_manager: 'OPERATIONS_MANAGER',
  financial_officer: 'FINANCIAL_OFFICER',
  support_agent: 'SUPPORT_AGENT',
  viewer: 'SUPPORT_AGENT',
};

/** Backend roles allowed into the admin backoffice. */
export const DASHBOARD_ROLES = new Set([
  'system_administrator',
  'operations_manager',
  'financial_officer',
  'support_agent',
]);

export function isDashboardRole(backendRole: BackendRole | null | undefined): boolean {
  return DASHBOARD_ROLES.has(String(backendRole ?? '').trim().toLowerCase());
}

export function toAppRole(backendRole: BackendRole | null | undefined): AppRole {
  const normalized = String(backendRole ?? '').trim().toLowerCase();
  return BACKEND_TO_APP_ROLE[normalized] ?? 'SUPPORT_AGENT';
}

export function getPermissions(role: AppRole): readonly Permission[] {
  return ROLE_PERMISSION_MAP[role] ?? SUPPORT_AGENT_PERMISSIONS;
}

export function hasPermission(role: AppRole, permission: Permission): boolean {
  return getPermissions(role).includes(permission);
}

export function backendRoleHasPermission(
  backendRole: BackendRole | null | undefined,
  permission: Permission,
): boolean {
  return hasPermission(toAppRole(backendRole), permission);
}

function normalizePath(pathname: string): string {
  const p = pathname.replace(/\/+$/, '') || '/';
  return p === '' ? '/' : p;
}

/**
 * Minimum permission required for the current URL.
 * Used by RouteGuard for deep links and live permission revocations.
 */
export function getRequiredPermissionForPath(pathname: string, search: string): Permission | undefined {
  const path = normalizePath(pathname);

  if (path === '/') {
    return 'CAN_VIEW_OVERVIEW';
  }
  if (path === '/brokers' || path.startsWith('/brokers/')) {
    if (path === '/brokers/new') {
      return 'CAN_CREATE_BROKER';
    }
    return 'CAN_VIEW_BROKERS';
  }
  if (path === '/deals/new' || path === '/brokerage/new') {
    return 'CAN_CREATE_DEAL';
  }
  if (
    path === '/deals' ||
    path.startsWith('/deals/') ||
    path === '/brokerage' ||
    path.startsWith('/brokerage/')
  ) {
    return 'CAN_VIEW_DEALS';
  }
  if (path === '/commission-reports') {
    return 'CAN_VIEW_REPORTS';
  }
  if (path === '/system-logs') {
    return 'CAN_VIEW_LOGS';
  }
  if (path === '/settings') {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const tab = (params.get('tab') || '').trim().toLowerCase();
    if (tab === 'accounts') {
      return 'CAN_MANAGE_ACCOUNTS';
    }
    if (tab === 'roles-permissions' || tab === 'roles') {
      return 'CAN_MANAGE_ROLES';
    }
    return 'CAN_VIEW_SETTINGS';
  }
  if (path === '/settings/accounts/new') {
    return 'CAN_MANAGE_ACCOUNTS';
  }
  if (path === '/settings/roles-permissions') {
    return 'CAN_MANAGE_ROLES';
  }
  if (path === '/withdrawals' || path.startsWith('/withdrawals/')) {
    return 'CAN_VIEW_OVERVIEW';
  }
  if (path === '/access-denied') {
    return undefined;
  }
  return undefined;
}

/** Nav items that require a specific permission. */
export const NAV_PERMISSION_MAP: Record<string, Permission | undefined> = {
  '/': 'CAN_VIEW_OVERVIEW',
  '/brokers': 'CAN_VIEW_BROKERS',
  '/brokerage': 'CAN_VIEW_DEALS',
  '/commission-reports': 'CAN_VIEW_REPORTS',
  '/withdrawals': 'CAN_VIEW_OVERVIEW',
  '/system-logs': 'CAN_VIEW_LOGS',
  '/settings': 'CAN_VIEW_SETTINGS',
};
